import {
  Component,
  ViewChild,
  AfterViewInit,
  TemplateRef,
  Inject,
  Renderer2,
} from '@angular/core';
import {
  FormlyFieldConfig,
  ɵdefineHiddenProp as defineHiddenProp,
} from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material/form-field';
import { MatInput } from '@angular/material/input';
import {
  MAT_DATETIME_FORMATS,
  MatDatetimepickerInputDirective,
} from '@mat-datetimepicker/core';
import {
  futureDateTimeValidation,
  futureDateValidation,
  pastDateTimeValidation,
  pastDateValidation,
} from '../../validators';
import { get, isEmpty } from 'lodash';
import { DOCUMENT } from '@angular/common';
import { asyncScheduler } from 'rxjs';

@Component({
  selector: 'app-formly-field-datetime',
  template: `
    <input
      matInput
      [id]="id"
      [formControl]="formControl"
      [errorStateMatcher]="errorStateMatcher"
      [matDatetimepicker]="datetimePicker"
      [max]="to.datetimepickerOptions.max"
      [min]="to.datetimepickerOptions.min"
      [formlyAttributes]="field"
      [placeholder]="to.placeholder"
      [tabindex]="to.tabindex"
      [readonly]="to.readonly"
      [required]="to.required"
    />
    <ng-template #datetimepickerToggle>
      <mat-datetimepicker-toggle
        [for]="datetimePicker"
        matSuffix
      ></mat-datetimepicker-toggle>
    </ng-template>
    <mat-datetimepicker
      #datetimePicker
      [type]="to.type"
      [disabled]="to.disabled || to.readonly"
      [openOnFocus]="to.datetimepickerOptions.openOnFocus"
      [timeInterval]="to.datetimepickerOptions.timeInterval"
      (opened)="onOpenPicker()"
      (opened)="onOpenPicker()"
    >
    </mat-datetimepicker>
  `,
  providers: [
    {
      provide: MAT_DATETIME_FORMATS,
      useValue: {
        parse: {
          dateInput: 'L',
          monthInput: 'MMMM',
          timeInput: 'LT',
          datetimeInput: 'L LT',
        },
        display: {
          dateInput: 'L',
          monthInput: 'MMMM',
          datetimeInput: 'L LT',
          timeInput: 'LT',
          monthYearLabel: 'MMM YYYY',
          dateA11yLabel: 'LL',
          monthYearA11yLabel: 'MMMM YYYY',
          popupHeaderDateLabel: 'ddd, DD MMM',
        },
      },
    },
  ],
})
export class FormlyFieldDateTimeComponent
  extends FieldType
  implements AfterViewInit
{
  @ViewChild(MatInput, { static: true } as any) formFieldControl!: MatInput;
  @ViewChild(MatDatetimepickerInputDirective)
  datetimepickerInput!: MatDatetimepickerInputDirective<any>;
  @ViewChild('datetimepickerToggle') datetimepickerToggle!: TemplateRef<any>;
  defaultOptions: FormlyFieldConfig = {
    templateOptions: {
      datetimepickerOptions: {
        type: 'datetime',
        startView: 'month',
        openOnFocus: false,
        timeInterval: 1,
        datetimepickerTogglePosition: 'suffix',
        dateInput: () => {},
        dateChange: () => {},
      },
    },
  };
  constructor(@Inject(DOCUMENT) private document: Document, private renderer: Renderer2) {
    super();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
    (this.datetimepickerInput as any)._formField = this.formField;

    // add validators only if dateValidation has a value in it
    if (this.to.dateValidation) {
      const validationCheck =
        this.to.dateValidation === 1
          ? get(this.field, 'templateOptions.type') === 'date'
            ? futureDateValidation
            : futureDateTimeValidation
          : get(this.field, 'templateOptions.type') === 'date'
          ? pastDateValidation
          : pastDateTimeValidation;
      this.formControl.addValidators([validationCheck]);
    }
    setTimeout(() => {
      defineHiddenProp(
        this.field,
        '_mat' + this.to.datetimepickerOptions.datetimepickerTogglePosition,
        this.datetimepickerToggle
      );
      (this.options as any)._markForCheck(this.field);
    });
  }

  onOpenPicker(): void {
    asyncScheduler.schedule(() => {
      const headerEl = this.document.querySelector('.mat-datetimepicker-calendar-header') as HTMLElement;
      const controlsEl = this.document.querySelector('.mat-datetimepicker-calendar-controls ') as HTMLElement;
      const calendarEL = this.document.querySelector(".mat-datetimepicker-calendar") as HTMLElement;
      const yearButton = headerEl.querySelector(".mat-datetimepicker-calendar-header-year") as HTMLElement;
      const monthButton = headerEl.querySelector(".mat-datetimepicker-calendar-header-date") as HTMLElement;
      const previousButton = controlsEl.querySelector(".mat-datetimepicker-calendar-previous-button") as HTMLElement;
      const nextButton = controlsEl.querySelector(".mat-datetimepicker-calendar-next-button") as HTMLElement;

      this.removeAccessibility(calendarEL);
      this.setAccessability(yearButton);
      this.setAccessability(monthButton);
      this.setAccessability(previousButton);
      this.addFocusStyles(previousButton, { borderRadius: '50%' });
      this.setAccessability(nextButton);
      this.addFocusStyles(nextButton, { borderRadius: '50%' });
    });
  }

  setAccessability(el: HTMLElement): void {
    if (!el) {
      return;
    }
    this.renderer.setAttribute(el, "tabindex", "0");
    this.renderer.listen(el, "keydown", (event: KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        asyncScheduler.schedule(() => {
          el.click();
        });
      }
    });
  }

  removeAccessibility(el: HTMLElement): void {
    if (!el) {
      return;
    }
    this.renderer.setAttribute(el, "tabindex", "-1");
  }

  addFocusStyles(el: HTMLElement, extraStyles?: Partial<CSSStyleDeclaration>): void {
    this.renderer.addClass(el, "focus-style");
    if (!isEmpty(extraStyles)) {
      for (const style in extraStyles) {
        this.renderer.setStyle(el, style, extraStyles[style]);
      }
    }
  }

}
